import React from "react";
import { useState, useEffect } from "react";
import { Info, Calculator } from "lucide-react";
import { cn } from "../../src/lib/utils";
import { useTranslation } from "../i18n";
import placeholderImg from "../assets/placeholder.svg";
import billingImg from "../assets/icons/billing.png";
import buddyImg from "../assets/icons/Buddy-removebg-preview.png";
import identityImg from "../assets/icons/Identity-removebg-preview.png";
import supportImg from "../assets/icons/Advanced_Customer_upport-removebg-preview.png";
import anomalyImg from "../assets/icons/Anomalydetection-removebg-preview.png";
import appIcon from "../assets/icons/app.png";
import webappIcon from "../assets/icons/webapp.png";
import dashboardIcon from "../assets/icons/dashboard.png";
import marketplaceIcon from "../assets/icons/marketplace.png";
import mapIcon from "../assets/icons/map.png";
import docsIcon from "../assets/icons/docs.png";

const IconSources: Record<string, string> = {
  app: appIcon,
  webapp: webappIcon,
  dashboard: dashboardIcon,
  marketplace: marketplaceIcon,
  map: mapIcon,
  docs: docsIcon,
};

type DeviceType = "iot" | "online" | "offline" | "bluetooth";

interface PriceTier {
  min: number;
  max: number | null;
  price: number;
}

interface DeviceTypeInfo {
  name: string;
  description: string;
  icon: React.ReactNode;
  tiers: PriceTier[];
  color: string;
}

interface PremiumService {
  id: string;
  name: string;
  description: string;
  pricing: string;
  pricingDetails: string;
  icon: string;
  available: boolean;
}

export function PricingSection() {
  const { t, browserLanguage, selectedLanguage } = useTranslation();
  const [activeTab, setActiveTab] = useState<DeviceType>("iot");
  const [showAdditionalServices, setShowAdditionalServices] = useState(false);
  const [hoveredInfo, setHoveredInfo] = useState<string | null>(null);
  const [showDeviceExplanation, setShowDeviceExplanation] = useState(false);

  // Price calculator state
  const [deviceCounts, setDeviceCounts] = useState({
    iot: 0,
    online: 0,
    offline: 0,
    bluetooth: 0,
  });
  const [selectedServices, setSelectedServices] = useState({
    buddy: false,
    identity: false,
  });
  const [estimatedPrice, setEstimatedPrice] = useState({
    devices: 0,
    services: 0,
    transactions: 0,
    total: 0,
  });
  const [transactionVolume, setTransactionVolume] = useState(0);
  const [averageBookingValue, setAverageBookingValue] = useState(0);

  const deviceTypes: Record<DeviceType, DeviceTypeInfo> = {
    iot: {
      name: t("bikeParking.pricingSection.deviceTypes.iot.name"),
      description: t("bikeParking.pricingSection.deviceTypes.iot.description"),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
          />
        </svg>
      ),
      tiers: [
        { min: 1, max: 50, price: 3.49 },
        { min: 51, max: 200, price: 3.25 },
        { min: 201, max: null, price: 2.99 },
      ],
      color: "purple",
    },
    online: {
      name: t("bikeParking.pricingSection.deviceTypes.online.name"),
      description: t(
        "bikeParking.pricingSection.deviceTypes.online.description"
      ),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
          />
        </svg>
      ),
      tiers: [
        { min: 1, max: 50, price: 2.99 },
        { min: 51, max: 200, price: 2.7 },
        { min: 201, max: null, price: 2.6 },
      ],
      color: "purple",
    },
    offline: {
      name: t("bikeParking.pricingSection.deviceTypes.offline.name"),
      description: t(
        "bikeParking.pricingSection.deviceTypes.offline.description"
      ),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
      tiers: [
        { min: 1, max: 50, price: 2.0 },
        { min: 51, max: 500, price: 1.8 },
        { min: 501, max: 1000, price: 1.7 },
        { min: 1001, max: null, price: 1.65 },
      ],
      color: "purple",
    },
    bluetooth: {
      name: t("bikeParking.pricingSection.deviceTypes.bluetooth.name"),
      description: t(
        "bikeParking.pricingSection.deviceTypes.bluetooth.description"
      ),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75-3.75h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
          />
        </svg>
      ),
      tiers: [
        { min: 1, max: 50, price: 5.49 },
        { min: 51, max: 200, price: 5.25 },
        { min: 201, max: null, price: 5.15 },
      ],
      color: "purple",
    },
  };

  const premiumServices: PremiumService[] = [
    {
      id: "buddy",
      name: t("bikeParking.pricingSection.premiumServices.buddy.name"),
      description: t(
        "bikeParking.pricingSection.premiumServices.buddy.description"
      ),
      pricing: t("bikeParking.pricingSection.premiumServices.buddy.pricing"),
      pricingDetails: t(
        "bikeParking.pricingSection.premiumServices.buddy.pricingDetails"
      ),
      icon: "/icons/Buddy-removebg-preview.png",
      available: true,
    },
    {
      id: "identity",
      name: t("bikeParking.pricingSection.premiumServices.identity.name"),
      description: t(
        "bikeParking.pricingSection.premiumServices.identity.description"
      ),
      pricing: t("bikeParking.pricingSection.premiumServices.identity.pricing"),
      pricingDetails: t(
        "bikeParking.pricingSection.premiumServices.identity.pricingDetails"
      ),
      icon: "/icons/Identity-removebg-preview.png",
      available: true,
    },
    {
      id: "support",
      name: t("bikeParking.pricingSection.premiumServices.support.name"),
      description: t(
        "bikeParking.pricingSection.premiumServices.support.description"
      ),
      pricing: t("bikeParking.pricingSection.premiumServices.support.pricing"),
      pricingDetails: t(
        "bikeParking.pricingSection.premiumServices.support.pricingDetails"
      ),
      icon: "/icons/Advanced_Customer_upport-removebg-preview.png",
      available: false,
    },
    {
      id: "maintenance",
      name: t("bikeParking.pricingSection.premiumServices.maintenance.name"),
      description: t(
        "bikeParking.pricingSection.premiumServices.maintenance.description"
      ),
      pricing: t(
        "bikeParking.pricingSection.premiumServices.maintenance.pricing"
      ),
      pricingDetails: t(
        "bikeParking.pricingSection.premiumServices.maintenance.pricingDetails"
      ),
      icon: "/icons/Anomalydetection-removebg-preview.png",
      available: false,
    },
  ];

  // Calculate savings based on base price
  const calculateSavings = (basePrice: number, currentPrice: number) => {
    return (((basePrice - currentPrice) / basePrice) * 100).toFixed(1);
  };

  // Get base price for the current device type
  const getBasePrice = (type: DeviceType) => {
    return deviceTypes[type].tiers[0].price;
  };

  // Get price for a specific device type and count
  const getDevicePrice = (type: DeviceType, count: number) => {
    if (count === 0) return 0;

    const tiers = deviceTypes[type].tiers;
    let price = 0;

    for (const tier of tiers) {
      if (tier.max === null || count <= tier.max) {
        price = tier.price * count;
        break;
      }
    }

    return price;
  };

  // Calculate transaction commission
  const calculateTransactionCommission = (volume: number, avgValue: number) => {
    if (volume === 0) return 0;

    const commissionRate = 0.1; // 10%
    const minFee = 0.5; // €0.50
    const maxFee = 5; // €5.00

    const commission = avgValue * commissionRate;
    const cappedCommission = Math.min(Math.max(commission, minFee), maxFee);

    return cappedCommission * volume;
  };

  // Update price calculation when inputs change
  useEffect(() => {
    const totalDevices = Object.values(deviceCounts).reduce(
      (sum, count) => sum + count,
      0
    );

    // Calculate device costs
    const deviceCosts = (Object.keys(deviceCounts) as DeviceType[]).reduce(
      (sum, type) => {
        return sum + getDevicePrice(type, deviceCounts[type]);
      },
      0
    );

    // No service costs as premium services are removed
    const serviceCosts = 0;

    // Calculate transaction costs
    const transactionCosts = calculateTransactionCommission(
      transactionVolume,
      averageBookingValue
    );

    // Update estimated price
    setEstimatedPrice({
      devices: Number.parseFloat(deviceCosts.toFixed(2)),
      services: 0,
      transactions: Number.parseFloat(transactionCosts.toFixed(2)),
      total: Number.parseFloat((deviceCosts + transactionCosts).toFixed(2)),
    });
  }, [deviceCounts, selectedServices, transactionVolume, averageBookingValue]);

  // Handle device count change
  const handleDeviceCountChange = (type: DeviceType, value: string) => {
    const count = Number.parseInt(value) || 0;
    setDeviceCounts((prev) => ({
      ...prev,
      [type]: count >= 0 ? count : 0,
    }));
  };

  // Handle service selection change
  const handleServiceChange = (id: string, checked: boolean) => {
    setSelectedServices((prev) => ({
      ...prev,
      [id]: checked,
    }));
  };

  return (
    <section className="relative py-24 bg-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-6xl md:text-8xl font-extrabold text-black -mt-6 mb-6 tracking-tight">
            {t("bikeParking.pricingSection.heading")}
          </h1>

          {/* Decorative image in background */}
          <div className="absolute inset-0 flex justify-center items-center opacity-10 pointer-events-none">
            <img
              src={placeholderImg}
              alt={t("bikeParking.pricingSection.decorativeAlt")}
              width={400}
              height={400}
              className="object-contain"
            />
          </div>

          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            {t("bikeParking.pricingSection.heroStart")}{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 bg-200 animate-gradient">
              {t("bikeParking.pricingSection.heroHighlight1")}
            </span>{" "}
            {t("bikeParking.pricingSection.heroMid")}{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 bg-200 animate-gradient">
              {t("bikeParking.pricingSection.heroHighlight2")}
            </span>{" "}
            {t("bikeParking.pricingSection.heroEnd")}
          </h2>

          <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
            {t("bikeParking.pricingSection.description")}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Active Device Type Pricing */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden transform transition-all hover:shadow-xl">
            <div className="bg-gradient-to-r from-purple-600 to-pink-500 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="mr-3 bg-purple-500/30 p-2 rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">
                      {t("bikeParking.pricingSection.deviceLicenseTitle")}
                    </h3>
                  </div>
                </div>
                <div
                  className="relative"
                  onMouseEnter={() => setHoveredInfo(activeTab)}
                  onMouseLeave={() => setHoveredInfo(null)}
                >
                  <button className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors duration-200">
                    <Info className="h-5 w-5 text-white" />
                  </button>

                  {hoveredInfo === activeTab && (
                    <div className="absolute right-0 mt-2 w-72 p-4 bg-white rounded-lg shadow-xl text-gray-800 text-sm z-10 transition-all duration-200 ease-in-out transform origin-top-right">
                      <div className="absolute top-0 right-4 transform -translate-y-1/2 rotate-45 w-3 h-3 bg-white"></div>
                      <p>{deviceTypes[activeTab].description}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Device Type Selector */}
            <div className="bg-white border-b border-gray-200 px-6 py-3">
              <div className="flex flex-wrap gap-2">
                {(Object.keys(deviceTypes) as DeviceType[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => setActiveTab(type)}
                    className={cn(
                      "px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ease-in-out",
                      activeTab === type
                        ? `bg-purple-600 text-white shadow-md`
                        : `bg-white border border-gray-200 text-gray-700 hover:bg-gray-50`
                    )}
                  >
                    {deviceTypes[type].name}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-8">
              <div className="mb-6">
                <p className="text-gray-600">
                  {t("bikeParking.pricingSection.deviceInfo")}
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="px-4 py-3 text-sm font-semibold text-gray-500 uppercase tracking-wider">
                        {t("bikeParking.pricingSection.table.volume")}
                      </th>
                      <th className="px-4 py-3 text-sm font-semibold text-gray-500 uppercase tracking-wider">
                        {t("bikeParking.pricingSection.table.price")}
                      </th>
                      <th className="px-4 py-3 text-sm font-semibold text-gray-500 uppercase tracking-wider">
                        {t("bikeParking.pricingSection.table.savings")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {deviceTypes[activeTab].tiers.map((tier, index) => {
                      const basePrice = getBasePrice(activeTab);
                      const savings =
                        index > 0
                          ? calculateSavings(basePrice, tier.price)
                          : "0";

                      return (
                        <tr
                          key={index}
                          className={cn(
                            "border-b border-gray-200 transition-colors",
                            index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          )}
                        >
                          <td className="px-4 py-4 text-sm font-medium">
                            {tier.max
                              ? `${tier.min} - ${tier.max}`
                              : `${tier.min}+`}
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-2xl font-bold text-gray-900">
                              €{tier.price.toFixed(2)}
                            </span>
                            <span className="text-sm text-gray-500 ml-1">
                              /month
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            {index > 0 ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-lime-100 text-lime-800">
                                {t("bikeParking.pricingSection.table.save", {
                                  savings,
                                })}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-500">
                                {t(
                                  "bikeParking.pricingSection.table.basePrice"
                                )}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <h5 className="font-semibold text-gray-900 mb-4">
                  {t("bikeParking.pricingSection.includedProducts")}
                </h5>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    {
                      name: t("bikeParking.pricingSection.modules.app"),
                      icon: "app",
                    },
                    {
                      name: t("bikeParking.pricingSection.modules.webapp"),
                      icon: "webapp",
                    },
                    {
                      name: t("bikeParking.pricingSection.modules.dashboard"),
                      icon: "dashboard",
                    },
                    {
                      name: t("bikeParking.pricingSection.modules.marketplace"),
                      icon: "marketplace",
                    },
                    {
                      name: t("bikeParking.pricingSection.modules.map"),
                      icon: "map",
                    },
                    {
                      name: t("bikeParking.pricingSection.modules.docs"),
                      icon: "docs",
                    },
                  ].map((module, index) => (
                    <div
                      key={index}
                      className="flex items-center bg-gray-50 rounded-lg p-2 transition-transform hover:scale-105"
                    >
                      <div className="w-8 h-8 flex-shrink-0 mr-2">
                        <img
                          src={IconSources[module.icon]}
                          alt={module.name}
                          width={32}
                          height={32}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {module.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-4 text-center">
                <button
                  onClick={() =>
                    setShowDeviceExplanation(!showDeviceExplanation)
                  }
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 shadow-sm transition-colors"
                >
                  {showDeviceExplanation
                    ? t("bikeParking.pricingSection.hideExplanation")
                    : t("bikeParking.pricingSection.showExplanation")}
                </button>
              </div>
            </div>
          </div>

          {/* Transaction Fees */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden transform transition-all hover:shadow-xl">
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="mr-3 bg-blue-500/30 p-2 rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">
                      {t("bikeParking.pricingSection.transactionTitle")}
                    </h3>
                  </div>
                </div>
                <div
                  className="relative"
                  onMouseEnter={() => setHoveredInfo("transaction")}
                  onMouseLeave={() => setHoveredInfo(null)}
                >
                  <button className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors duration-200">
                    <Info className="h-5 w-5 text-white" />
                  </button>

                  {hoveredInfo === "transaction" && (
                    <div className="absolute right-0 mt-2 w-72 p-4 bg-white rounded-lg shadow-xl text-gray-800 text-sm z-10 transition-all duration-200 ease-in-out transform origin-top-right">
                      <div className="absolute top-0 right-4 transform -translate-y-1/2 rotate-45 w-3 h-3 bg-white"></div>
                      <p>{t("bikeParking.pricingSection.transactionInfo")}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Empty div to match height of device selector */}
            <div className="bg-white border-b border-gray-200 px-6 py-3">
              <h4 className="text-xl font-semibold text-gray-900">
                {t("bikeParking.pricingSection.payOnlyLabel")}
              </h4>
              <p className="text-gray-600 text-sm">
                {t("bikeParking.pricingSection.payOnlyText")}
              </p>
            </div>

            <div className="p-8">
              <div className="mb-6">
                {/* Content moved above the divider line */}
              </div>

              <div className="flex flex-col items-center justify-center py-8 bg-gradient-to-b from-gray-50 to-white rounded-xl mb-6">
                <div className="text-center">
                  <p className="flex items-baseline justify-center">
                    <span className="text-6xl font-bold tracking-tight text-gray-900">
                      10%
                    </span>
                    <span className="ml-2 text-xl font-medium text-gray-500">
                      of booking value
                    </span>
                  </p>
                  <p className="mt-2 text-sm text-gray-500">
                    Minimum €0.50 — Maximum €5 per transaction
                  </p>
                </div>
              </div>

              <div className="mt-6 border-t border-gray-200 pt-6">
                <h5 className="font-semibold text-gray-900 mb-4">
                  {t("bikeParking.pricingSection.examples")}
                </h5>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 shadow-sm transition-transform hover:scale-105">
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold text-gray-900">
                        €10 {t("bikeParking.pricingSection.booking")}
                      </span>
                      <br />
                      <span className="text-lime-600 font-medium">
                        {t("bikeParking.pricingSection.fee", {
                          amount: "€1.00",
                        })}
                      </span>
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 shadow-sm transition-transform hover:scale-105">
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold text-gray-900">
                        €30 {t("bikeParking.pricingSection.booking")}
                      </span>
                      <br />
                      <span className="text-lime-600 font-medium">
                        {t("bikeParking.pricingSection.fee", {
                          amount: "€3.00",
                        })}
                      </span>
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 shadow-sm transition-transform hover:scale-105">
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold text-gray-900">
                        €60 {t("bikeParking.pricingSection.booking")}
                      </span>
                      <br />
                      <span className="text-lime-600 font-medium">
                        {t("bikeParking.pricingSection.feeMax", {
                          amount: "€5.00",
                        })}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <h5 className="font-semibold text-gray-900 mb-4">
                  {t("bikeParking.pricingSection.includedProducts")}
                </h5>
                <div className="flex items-center bg-gray-50 rounded-lg p-3 transition-transform hover:scale-105 w-fit">
                  <div className="w-8 h-8 flex-shrink-0 mr-2">
                    <img
                      src={billingImg}
                      alt={t("bikeParking.pricingSection.modules.billing")}
                      width={32}
                      height={32}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {t("bikeParking.pricingSection.modules.billing")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Device Type Explanation - Collapsible */}
        {showDeviceExplanation && (
          <div className="mt-10 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-8 border border-purple-200 shadow-md transition-all duration-300 ease-in-out">
            <div className="flex">
              <Info className="h-6 w-6 text-purple-500 flex-shrink-0 mt-1" />
              <div className="ml-4">
                <h4 className="text-lg font-semibold text-gray-900">
                  {t("bikeParking.pricingSection.explanationTitle")}
                </h4>
                <p className="mt-2 text-gray-600">
                  {t("bikeParking.pricingSection.explanationIntro")}
                </p>
                <ul className="mt-4 space-y-3">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-purple-100 text-purple-600 text-xs font-medium mr-3 mt-0.5">
                      1
                    </span>
                    <span className="text-gray-600">
                      <strong>
                        {t("bikeParking.pricingSection.deviceTypes.iot.name")}:
                      </strong>{" "}
                      {t("bikeParking.pricingSection.explanationPoints.iot")}
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-purple-100 text-purple-600 text-xs font-medium mr-3 mt-0.5">
                      2
                    </span>
                    <span className="text-gray-600">
                      <strong>
                        {t(
                          "bikeParking.pricingSection.deviceTypes.offline.name"
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "bikeParking.pricingSection.explanationPoints.offline"
                      )}
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-purple-100 text-purple-600 text-xs font-medium mr-3 mt-0.5">
                      3
                    </span>
                    <span className="text-gray-600">
                      <strong>
                        {t(
                          "bikeParking.pricingSection.deviceTypes.online.name"
                        )}
                        :
                      </strong>{" "}
                      {t("bikeParking.pricingSection.explanationPoints.online")}
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-purple-100 text-purple-600 text-xs font-medium mr-3 mt-0.5">
                      4
                    </span>
                    <span className="text-gray-600">
                      <strong>
                        {t(
                          "bikeParking.pricingSection.deviceTypes.bluetooth.name"
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "bikeParking.pricingSection.explanationPoints.bluetooth"
                      )}
                    </span>
                  </li>
                </ul>
                <p className="mt-4 text-gray-600">
                  {t("bikeParking.pricingSection.volumeDiscount")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Premium Services */}
        <div className="mt-16">
          <div className="text-center mb-10">
            <h3 className="text-2xl font-bold text-gray-900">
              {t("bikeParking.pricingSection.premiumTitle")}
            </h3>
            <p className="mt-2 text-xl text-gray-600">
              {t("bikeParking.pricingSection.premiumDesc")}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {/* JUHUU Buddy */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-pink-500 px-6 py-4 text-white">
                <div className="flex items-center">
                  <div className="mr-3 bg-white p-2 rounded-lg shadow-sm">
                    <img
                      src={buddyImg}
                      alt={t(
                        "bikeParking.pricingSection.premiumServices.buddy.name"
                      )}
                      width={28}
                      height={28}
                      className="h-7 w-7"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">
                      {t(
                        "bikeParking.pricingSection.premiumServices.buddy.name"
                      )}
                    </h3>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <p className="text-2xl font-bold tracking-tight text-gray-900">
                    {t(
                      "bikeParking.pricingSection.premiumServices.buddy.pricing"
                    )}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {t(
                      "bikeParking.pricingSection.premiumServices.buddy.pricingDetails"
                    )}
                  </p>
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  {t(
                    "bikeParking.pricingSection.premiumServices.buddy.description"
                  )}
                </div>
              </div>
            </div>

            {/* Identity Verification */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-pink-500 px-6 py-4 text-white">
                <div className="flex items-center">
                  <div className="mr-3 bg-white p-2 rounded-lg shadow-sm">
                    <img
                      src={identityImg}
                      alt={t(
                        "bikeParking.pricingSection.premiumServices.identity.name"
                      )}
                      width={28}
                      height={28}
                      className="h-7 w-7"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">
                      {t(
                        "bikeParking.pricingSection.premiumServices.identity.name"
                      )}
                    </h3>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <p className="flex items-baseline">
                    <span className="text-2xl font-bold tracking-tight text-gray-900">
                      {t(
                        "bikeParking.pricingSection.premiumServices.identity.pricing"
                      )}
                    </span>
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {t(
                      "bikeParking.pricingSection.premiumServices.identity.pricingDetails"
                    )}
                  </p>
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  {t(
                    "bikeParking.pricingSection.premiumServices.identity.description"
                  )}
                </div>
              </div>
            </div>

            {/* Advanced Customer Support - Coming Soon */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-pink-500 px-6 py-4 text-white relative">
                <div className="absolute top-0 right-0 bg-yellow-400 text-xs font-bold px-3 py-1 text-white rounded-bl-2xl">
                  {t("bikeParking.pricingSection.comingSoon")}
                </div>
                <div className="flex items-center">
                  <div className="mr-3 bg-white p-2 rounded-lg shadow-sm">
                    <img
                      src={supportImg}
                      alt={t(
                        "bikeParking.pricingSection.premiumServices.support.name"
                      )}
                      width={28}
                      height={28}
                      className="h-7 w-7"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold leading-tight h-14 flex flex-col justify-center">
                      <span className="block">
                        {t(
                          "bikeParking.pricingSection.premiumServices.support.name"
                        )}
                      </span>
                    </h3>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <p className="flex items-baseline filter blur-sm">
                    <span className="text-2xl font-bold tracking-tight text-gray-900">
                      {t(
                        "bikeParking.pricingSection.premiumServices.support.pricing"
                      )}
                    </span>
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {t(
                      "bikeParking.pricingSection.premiumServices.support.pricingDetails"
                    )}
                  </p>
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  {t(
                    "bikeParking.pricingSection.premiumServices.support.description"
                  )}
                </div>
              </div>
            </div>

            {/* Predictive Maintenance - Coming Soon */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-pink-500 px-6 py-4 text-white relative">
                <div className="absolute top-0 right-0 bg-yellow-400 text-xs font-bold px-3 py-1 text-white rounded-bl-2xl">
                  {t("bikeParking.pricingSection.comingSoon")}
                </div>
                <div className="flex items-center">
                  <div className="mr-3 bg-white p-2 rounded-lg shadow-sm">
                    <img
                      src={anomalyImg}
                      alt={t(
                        "bikeParking.pricingSection.premiumServices.maintenance.name"
                      )}
                      width={28}
                      height={28}
                      className="h-7 w-7"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold leading-tight h-14 flex flex-col justify-center">
                      <span className="block">
                        {t(
                          "bikeParking.pricingSection.premiumServices.maintenance.name"
                        )}
                      </span>
                    </h3>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <p className="flex items-baseline filter blur-sm">
                    <span className="text-2xl font-bold tracking-tight text-gray-900">
                      {t(
                        "bikeParking.pricingSection.premiumServices.maintenance.pricing"
                      )}
                    </span>
                    <span className="ml-1 text-base font-medium text-gray-500">
                      {t(
                        "bikeParking.pricingSection.premiumServices.maintenance.perDevice"
                      )}
                    </span>
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {t(
                      "bikeParking.pricingSection.premiumServices.maintenance.pricingDetails"
                    )}
                  </p>
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  {t(
                    "bikeParking.pricingSection.premiumServices.maintenance.description"
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Price Calculator */}
        <div className="mt-16">
          <div className="text-center mb-10">
            <h3 className="text-2xl font-bold text-gray-900">
              {t("bikeParking.pricingSection.calculatorTitle")}
            </h3>
            <p className="mt-2 text-xl text-gray-600">
              {t("bikeParking.pricingSection.calculatorDesc")}
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-pink-500 px-6 py-4 text-white">
              <div className="flex items-center">
                <div className="mr-3 bg-purple-500/30 p-2 rounded-full">
                  <Calculator className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">
                    {t("bikeParking.pricingSection.simulatorTitle")}
                  </h3>
                </div>
              </div>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Input Section */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-6">
                    {t("bikeParking.pricingSection.configuration")}
                  </h4>

                  {/* Device Counts */}
                  <div className="mb-8">
                    <h5 className="font-medium text-gray-800 mb-3">
                      {t("bikeParking.pricingSection.deviceTypesTitle")}
                    </h5>
                    <div className="space-y-4">
                      {(Object.keys(deviceTypes) as DeviceType[]).map(
                        (type) => (
                          <div key={type} className="flex items-center">
                            <label
                              htmlFor={`device-${type}`}
                              className="flex-1 text-sm font-medium text-gray-700"
                            >
                              {deviceTypes[type].name}
                              <span className="block text-xs text-gray-500 mt-0.5">
                                {deviceTypes[type].tiers[0].price.toFixed(2)}
                                €/month each
                              </span>
                            </label>
                            <div className="w-32">
                              <input
                                type="number"
                                id={`device-${type}`}
                                min="0"
                                value={deviceCounts[type] || ""}
                                onChange={(e) =>
                                  handleDeviceCountChange(type, e.target.value)
                                }
                                className="block w-full rounded-md border-2 border-purple-200 bg-purple-50 py-2 px-3 text-lg font-medium shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                              />
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  {/* Premium Services */}

                  {/* Transaction Volume */}
                  <div className="mb-8">
                    <h5 className="font-medium text-gray-800 mb-3">
                      {t("bikeParking.pricingSection.monthlyTransactions")}
                    </h5>
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <label
                          htmlFor="transaction-volume"
                          className="flex-1 text-sm font-medium text-gray-700"
                        >
                          {t("bikeParking.pricingSection.numberOfBookings")}
                          <span className="block text-xs text-gray-500 mt-0.5">
                            {t(
                              "bikeParking.pricingSection.commissionPerBooking"
                            )}
                          </span>
                        </label>
                        <div className="w-32">
                          <input
                            type="number"
                            id="transaction-volume"
                            min="0"
                            value={transactionVolume || ""}
                            onChange={(e) =>
                              setTransactionVolume(
                                Number.parseInt(e.target.value) || 0
                              )
                            }
                            className="block w-full rounded-md border-2 border-purple-200 bg-purple-50 py-2 px-3 text-lg font-medium shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                          />
                        </div>
                      </div>
                      <div className="flex items-center">
                        <label
                          htmlFor="avg-booking-value"
                          className="flex-1 text-sm font-medium text-gray-700"
                        >
                          {t("bikeParking.pricingSection.avgBookingValue")}
                          <span className="block text-xs text-gray-500 mt-0.5">
                            {t("bikeParking.pricingSection.minMaxFee")}
                          </span>
                        </label>
                        <div className="w-32">
                          <input
                            type="number"
                            id="avg-booking-value"
                            min="1"
                            value={averageBookingValue || ""}
                            onChange={(e) =>
                              setAverageBookingValue(
                                Number.parseInt(e.target.value) || 0
                              )
                            }
                            className="block w-full rounded-md border-2 border-purple-200 bg-purple-50 py-2 px-3 text-lg font-medium shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Results Section */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-6">
                    {t("bikeParking.pricingSection.estimatedTitle")}
                  </h4>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <div>
                        <h5 className="font-medium text-gray-800">
                          {t("bikeParking.pricingSection.deviceLicenses")}
                        </h5>
                        <p className="text-sm text-gray-500">
                          {Object.values(deviceCounts).reduce(
                            (sum, count) => sum + count,
                            0
                          )}{" "}
                          {t("bikeParking.pricingSection.devicesTotal")}
                        </p>
                      </div>
                      <div className="text-xl font-bold text-gray-900">
                        €{estimatedPrice.devices.toFixed(2)}
                      </div>
                    </div>

                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <div>
                        <h5 className="font-medium text-gray-800">
                          {t(
                            "bikeParking.pricingSection.transactionCommissions"
                          )}
                        </h5>
                        <p className="text-sm text-gray-500">
                          {transactionVolume} bookings × avg. €
                          {averageBookingValue}
                        </p>
                      </div>
                      <div className="text-xl font-bold text-gray-900">
                        €{estimatedPrice.transactions.toFixed(2)}
                      </div>
                    </div>

                    <div className="flex justify-between items-center py-4 mt-2">
                      <div>
                        <h5 className="text-lg font-semibold text-gray-900">
                          {t("bikeParking.pricingSection.totalMonthlyCost")}
                        </h5>
                      </div>
                      <div className="text-2xl font-bold text-purple-600">
                        €{estimatedPrice.total.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 bg-purple-50 rounded-lg p-4 border border-purple-100">
                    <div className="flex">
                      <Info className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
                      <div className="ml-3">
                        <p className="text-sm text-gray-600">
                          {t("bikeParking.pricingSection.estimateDisclaimer")}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 text-center">
                    <a
                      type="button"
                      href="#contact"
                      className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 shadow-sm transition-colors"
                    >
                      {t("bikeParking.pricingSection.contactSales")}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Note - Smaller version */}
        <div className="mt-8 max-w-2xl mx-auto">
          <div className="flex items-start bg-gray-50 rounded-lg p-3 border border-gray-200 text-xs">
            <Info className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="ml-2 text-gray-500 text-xs">
              {t("bikeParking.pricingSection.note")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
